/**
 * Web Audio API-based sound system for precise audio timing
 * Replaces HTMLAudioElement for better performance and sync accuracy
 */

const basePath = '/media/sound'

export const soundSources = {
  open: `${basePath}/open.ogg`,
  reveal: `${basePath}/reveal.ogg`,
  tick: `${basePath}/tick.ogg`,
  'wordle-click': `${basePath}/wordle-click.mp3`,
  'wordle-feed': `${basePath}/wordle-feed.mp3`,
  'wordle-feed2': `${basePath}/wordle-feed2.mp3`,
} as const

export type SoundName = keyof typeof soundSources

const soundEntries = Object.entries(soundSources) as Array<[SoundName, string]>

type AudioBufferCache = Partial<Record<SoundName, AudioBuffer | null>>

class WebAudioPlayer {
  private audioContext: AudioContext | null = null
  private buffers: AudioBufferCache = {}
  private volume: number = 0.15
  private initialized: boolean = false

  /**
   * Initialize the Web Audio context and load all sound buffers
   * Must be called after user interaction due to browser autoplay policies
   */
  async initialize(): Promise<void> {
    if (this.initialized) return

    try {
      // Create audio context
      const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext
      if (!AudioContextClass) {
        throw new Error('Web Audio API not supported')
      }

      this.audioContext = new AudioContextClass()

      // Resume immediately if suspended
      if (this.audioContext.state === 'suspended') {
        await this.audioContext.resume()
      }

      // Load and decode all sound files
      await Promise.all(
        soundEntries.map(async ([soundName, url]) => {
          try {
            const response = await fetch(url)
            if (!response.ok) {
              throw new Error(`HTTP ${response.status}: ${response.statusText}`)
            }
            const arrayBuffer = await response.arrayBuffer()
            const audioBuffer = await this.audioContext!.decodeAudioData(arrayBuffer)
            this.buffers[soundName] = audioBuffer
          } catch (error) {
            this.buffers[soundName] = null
          }
        })
      )

      this.initialized = true
    } catch (error) {
      console.error('Failed to initialize Web Audio:', error)
      this.initialized = false
      throw error
    }
  }

  /**
   * Resume audio context if suspended (required on mobile after user interaction)
   */
  async resume(): Promise<void> {
    if (this.audioContext && this.audioContext.state === 'suspended') {
      await this.audioContext.resume()
    }
  }

  /**
   * Play a sound immediately
   * @param soundName - Name of the sound to play
   * @returns AudioBufferSourceNode if successful, null otherwise
   */
  play(soundName: SoundName): AudioBufferSourceNode | null {
    if (!this.audioContext || !this.initialized) {
      console.warn('Web Audio not initialized')
      return null
    }

    const buffer = this.buffers[soundName]
    if (!buffer) {
      console.warn(`Sound buffer not loaded: ${soundName}`)
      return null
    }

    try {
      // Create buffer source
      const source = this.audioContext.createBufferSource()
      source.buffer = buffer

      // Create gain node for volume control
      const gainNode = this.audioContext.createGain()
      gainNode.gain.value = this.volume

      // Connect: source -> gain -> destination
      source.connect(gainNode)
      gainNode.connect(this.audioContext.destination)

      // Start immediately
      source.start(0)

      return source
    } catch (error) {
      console.warn(`Failed to play sound "${soundName}":`, error)
      return null
    }
  }

  /**
   * Schedule a sound to play at a specific time
   * @param soundName - Name of the sound to play
   * @param when - Absolute time in AudioContext time (use audioContext.currentTime + offset)
   * @returns AudioBufferSourceNode if successful, null otherwise
   */
  schedulePlay(soundName: SoundName, when: number): AudioBufferSourceNode | null {
    if (!this.audioContext || !this.initialized) {
      console.warn('Web Audio not initialized')
      return null
    }

    const buffer = this.buffers[soundName]
    if (!buffer) {
      console.warn(`Sound buffer not loaded: ${soundName}`)
      return null
    }

    try {
      // Create buffer source
      const source = this.audioContext.createBufferSource()
      source.buffer = buffer

      // Create gain node for volume control
      const gainNode = this.audioContext.createGain()
      gainNode.gain.value = this.volume

      // Connect: source -> gain -> destination
      source.connect(gainNode)
      gainNode.connect(this.audioContext.destination)

      // Schedule for future playback
      source.start(when)

      return source
    } catch (error) {
      console.warn(`Failed to schedule sound "${soundName}":`, error)
      return null
    }
  }

  /**
   * Get current audio context time
   */
  getCurrentTime(): number {
    return this.audioContext?.currentTime || 0
  }

  /**
   * Check if Web Audio is initialized and ready
   */
  isReady(): boolean {
    return this.initialized && this.audioContext !== null
  }

  /**
   * Set playback volume (0.0 to 1.0)
   */
  setVolume(volume: number): void {
    this.volume = Math.max(0, Math.min(1, volume))
  }
}

// Singleton instance
export const webAudioPlayer = new WebAudioPlayer()

/**
 * Initialize the Web Audio system
 * Should be called after user interaction
 */
export async function initializeWebAudio(): Promise<void> {
  await webAudioPlayer.initialize()
}

/**
 * Resume audio context (needed on mobile)
 */
export async function resumeWebAudio(): Promise<void> {
  await webAudioPlayer.resume()
}
